import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";

/**
 * Premium enterprise product card, used on the homepage + Products listing page.
 *
 * Props:
 *   name         — e.g. "SYNQ Projects"
 *   subtitle     — e.g. "Project Intelligence Platform"
 *   description  — 1-2 sentence description
 *   to           — route (learn more)
 *   accentClass  — tailwind gradient/color class for the abstract corner (opt.)
 *   badge        — "Live" | "Coming Soon" | null
 *   icon         — optional lucide icon element
 */
export default function ProductCard({ name, subtitle, description, to, accentClass, badge, icon }) {
  const disabled = badge === "Coming Soon";
  const Wrap = disabled ? "div" : Link;
  const wrapProps = disabled ? {} : { to };

  return (
    <Wrap
      {...wrapProps}
      data-testid={`product-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className={`group relative overflow-hidden rounded-3xl border ${
        disabled
          ? "border-slate-200 dark:border-slate-800 opacity-70 cursor-default"
          : "border-slate-200 dark:border-slate-800 hover:border-slate-900 dark:hover:border-white transition-colors cursor-pointer"
      } bg-white dark:bg-slate-900 p-8 flex flex-col`}
    >
      {/* Abstract intelligence accent — glowing corner */}
      <div
        aria-hidden
        className={`absolute -right-16 -top-16 w-56 h-56 rounded-full blur-3xl opacity-30 pointer-events-none ${
          accentClass || "bg-blue-200 dark:bg-blue-900/50"
        }`}
      />
      {/* Subtle grid pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(rgba(15,23,42,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.6) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative z-10 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center">
            {icon || <Sparkles className="w-5 h-5" />}
          </div>
          {badge && (
            <span
              className={`font-ibm-plex text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${
                badge === "Live"
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
              }`}
            >
              {badge}
            </span>
          )}
        </div>

        <h3 className="font-outfit text-2xl font-black text-slate-900 dark:text-white leading-tight">{name}</h3>
        <p className="font-ibm-plex text-sm text-slate-500 dark:text-slate-400 mt-1 mb-4">{subtitle}</p>
        <p className="font-ibm-plex text-[15px] leading-relaxed text-slate-700 dark:text-slate-300 flex-1">
          {description}
        </p>

        {!disabled && (
          <div className="mt-6 flex items-center gap-1.5 text-slate-900 dark:text-white font-outfit font-semibold text-sm group-hover:gap-2.5 transition-all">
            Learn more
            <ArrowUpRight className="w-4 h-4" />
          </div>
        )}
      </div>
    </Wrap>
  );
}
